import { FilterDefs, multiFilter } from "@homebound/beam";
import { CostConfigurationSummariesFilter, PotentialBidPlanDimensionKind } from "src/generated/graphqlTypes";
import { AllConfigurations_PotentialBidPlanDimensionResultFragment } from "./AllConfigurationsTab.generated";

export const ALL_CONFIGURATIONS_PAGE_SIZE = 200;

/** The offering filter plus one selection per dimension, keyed by the dimension's GlobalOptionType id. */
export type AllConfigurationsFilter = { productOffering?: string[] } & Record<string, string[] | undefined>;

export type TypeDimension = AllConfigurations_PotentialBidPlanDimensionResultFragment & {
  type: NonNullable<AllConfigurations_PotentialBidPlanDimensionResultFragment["type"]>;
};
type NamedOption = { id: string; name: string };

/** The dimensions backed by a GlobalOptionType, which drive the table's columns and filters. */
export function typeDimensions(
  dimensions: AllConfigurations_PotentialBidPlanDimensionResultFragment[],
): TypeDimension[] {
  return dimensions.filter(
    (d): d is TypeDimension => d.kind === PotentialBidPlanDimensionKind.GlobalOptionType && !!d.type,
  );
}

/** One multi-select per dimension, whose values are GlobalOption ids since those are shared across offerings. */
export function createFilterDefs(
  productOfferings: NamedOption[],
  dimensions: AllConfigurations_PotentialBidPlanDimensionResultFragment[],
): FilterDefs<AllConfigurationsFilter> {
  return {
    productOffering: namedFilter("Product Offering", productOfferings),
    ...typeDimensions(dimensions).mapToObject((d) => [d.type.id, namedFilter(d.displayName, d.options)]),
  };
}

function namedFilter(label: string, options: NamedOption[]) {
  return multiFilter({ label, options, getOptionValue: (o) => o.id, getOptionLabel: (o) => o.name });
}

/**
 * Maps the table's filter to the query's filter. An unselected filter means every value, so the offering filter
 * always sends the active offerings, which keeps archived and draft offerings out of the table.
 */
export function mapToFilter(
  filter: AllConfigurationsFilter,
  searchString: string | undefined,
  activeProductOfferingIds: string[],
): CostConfigurationSummariesFilter {
  const { productOffering, ...dimensions } = filter;
  const selected = Object.values(dimensions)
    .compact()
    .filter((globalOptions) => globalOptions.nonEmpty);
  return {
    productOfferings: productOffering?.nonEmpty ? productOffering : activeProductOfferingIds,
    ...(selected.nonEmpty && { dimensions: selected.map((globalOptions) => ({ globalOptions })) }),
    ...(searchString && { search: searchString }),
  };
}
